import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import apiUrls from "../../../config/api-urls";
import tokenService from "../../../services/token-service";

const DisputeDialog = ({ isOpen, onClose, taskId, userType = "handyman" }) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const disputeReasons = [
    "Payment issue",
    "Work quality dispute",
    "Incomplete work",
    "Customer not available",
    "Wrong service requested",
    "Safety concerns",
    "Other",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    if (!description.trim()) {
      toast.error("Please provide a description");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = tokenService.getToken();
      const response = await fetch(apiUrls.dispute.raiseDispute, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          taskId,
          reason,
          description,
          raisedBy: userType,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Dispute raised successfully. Our team will review it.");
        setReason("");
        setDescription("");
        onClose(true);
        
        // Navigate to appropriate screen based on user type
        setTimeout(() => {
          if (userType === "customer") {
            navigate("/customer/jobs");
          } else {
            navigate("/handyman/tasks");
          }
        }, 1000);
      } else {
        throw new Error(data.message || "Failed to raise dispute");
      }
    } catch (error) {
      toast.error(error.message || "Failed to raise dispute");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !isSubmitting && onClose()}
      />

      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm z-10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Raise Dispute
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Report an issue with this job
              </p>
            </div>
          </div>
          <button
            onClick={() => !isSubmitting && onClose()}
            disabled={isSubmitting}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Reason for Dispute *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
              required
            >
              <option value="">Select a reason</option>
              {disputeReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              placeholder="Please provide detailed information about the issue..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 resize-none"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Minimum 20 characters
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> Once a dispute is raised, our support team will review it and contact both parties. The job status will be marked as "Disputed" until resolved.
            </p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || description.length < 20}
            className="w-full px-4 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Raise Dispute"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DisputeDialog;
